import { getRadius, Box as MantineBox } from "@mantine/core";
import { useDidUpdate } from "@mantine/hooks";
import classNames from "classnames";
import * as React from "react";

import { type ElementProps, rem, type SizeValue, type StyleProps } from "../primitives/style-props";
import { tokens } from "../tokens";

import classes from "./media.module.css";

export interface ImageProps extends Omit<ElementProps<"div">, "placeholder">, StyleProps {
  src?: string | null;
  alt?: string;
  fit?: React.CSSProperties["objectFit"];
  width?: number | string;
  height?: number | string;
  radius?: SizeValue;
  withPlaceholder?: boolean;
  placeholder?: React.ReactNode;
  imageProps?: Omit<React.ComponentPropsWithoutRef<"img">, "className">;
  imageRef?: React.ForwardedRef<HTMLImageElement>;
  caption?: React.ReactNode;
  /** Owned class for the figure slot. */
  figureClassName?: string;
  /** Owned class for the image wrapper slot. */
  imageWrapperClassName?: string;
  /** Owned class for the image slot. */
  imageClassName?: string;
}

/** Mantine 6's default Image placeholder glyph. */
const IMAGE_ICON_PATH = [
  "M2.5 1H12.5C13.3284 1 14 1.67157 14 2.5V12.5C14 13.3284 13.3284 14 12.5 14H2.5",
  "C1.67157 14 1 13.3284 1 12.5V2.5C1 1.67157 1.67157 1 2.5 1ZM2.5 2C2.22386 2 2 2.22386 2 2.5V8.3636",
  "L3.6818 6.6818C3.76809 6.59551 3.88572 6.54797 4.00774 6.55007",
  "C4.12975 6.55216 4.24568 6.60372 4.32895 6.69293L7.87355 10.4901L10.6818 7.6818",
  "C10.8575 7.50607 11.1425 7.50607 11.3182 7.6818L13 9.3636V2.5C13 2.22386 12.7761 2 12.5 2H2.5ZM2 12.5",
  "V9.6364L3.98887 7.64753L7.5311 11.4421L8.94113 13H2.5C2.22386 13 2 12.7761 2 12.5ZM12.5 13H10.155",
  "L8.48336 11.153L11 8.6364L13 10.6364V12.5C13 12.7761 12.7761 13 12.5 13ZM6.64922 5.5",
  "C6.64922 5.03013 7.03013 4.64922 7.5 4.64922C7.96987 4.64922 8.35078 5.03013 8.35078 5.5",
  "C8.35078 5.96987 7.96987 6.35078 7.5 6.35078C7.03013 6.35078 6.64922 5.96987 6.64922 5.5ZM7.5 3.74922",
  "C6.53307 3.74922 5.74922 4.53307 5.74922 5.5C5.74922 6.46693 6.53307 7.25078 7.5 7.25078",
  "C8.46693 7.25078 9.25078 6.46693 9.25078 5.5C9.25078 4.53307 8.46693 3.74922 7.5 3.74922Z",
].join("");

const CAPTION_STYLE: React.CSSProperties = { textAlign: "center", lineHeight: tokens.typography.textLineHeight.sm };

const ImageIcon = (): JSX.Element => (
  <svg viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: rem(40), height: rem(40) }}>
    <path d={IMAGE_ICON_PATH} fill="currentColor" fillRule="evenodd" clipRule="evenodd" />
  </svg>
);

/**
 * Mantine 7's Image is a bare `<img>`, so this composite keeps Mantine 6's figure, placeholder,
 * caption, and source/error transition while the public `~/ui` contract stays unchanged.
 */
export const Image = React.forwardRef<HTMLDivElement, ImageProps>(function Image(
  {
    src,
    alt,
    fit = "cover",
    width = "100%",
    height = "auto",
    radius = 0,
    withPlaceholder,
    placeholder,
    imageProps,
    imageRef,
    caption,
    figureClassName,
    imageWrapperClassName,
    imageClassName,
    style,
    ...props
  },
  ref,
) {
  const [error, setError] = React.useState(!src);
  const isPlaceholder = withPlaceholder && error;
  const borderRadius = getRadius(radius);

  useDidUpdate(() => {
    setError(!src);
  }, [src]);

  return (
    <MantineBox {...props} style={{ width: rem(width), ...style }} ref={ref}>
      <figure className={classNames(classes.figure, figureClassName)}>
        <div className={classNames(classes.imageWrapper, imageWrapperClassName)}>
          <img
            src={src ?? undefined}
            alt={alt}
            ref={imageRef}
            {...imageProps}
            className={classNames(classes.image, imageClassName)}
            onError={event => {
              setError(true);
              imageProps?.onError?.(event);
            }}
            style={{
              objectFit: fit,
              width: rem(width),
              height: rem(height),
              borderRadius,
              ...(isPlaceholder ? { overflow: "hidden" } : {}),
              ...imageProps?.style,
            }}
          />
          {isPlaceholder && (
            <div className={classes.placeholder} title={alt} style={{ borderRadius }}>
              {placeholder || (
                <div>
                  <ImageIcon />
                </div>
              )}
            </div>
          )}
        </div>
        {!!caption && (
          <figcaption className={classes.caption} style={CAPTION_STYLE}>
            {caption}
          </figcaption>
        )}
      </figure>
    </MantineBox>
  );
});
